import { useCallback, useEffect, useRef } from 'react'
import { updateUrlFilters } from '../../../utils/urlFilters'

const ISSUE_PARAM = 'issue'
const COLLECT_PARAM = 'collect'
const NUMERIC_ID_RE = /^\d+$/u

const readParam = param => new URLSearchParams(window.location.search).get(param)
const parseNumericParam = raw => raw && NUMERIC_ID_RE.test(raw) ? Number(raw) : null

const clearIssueParams = () => updateUrlFilters({ [ISSUE_PARAM]: null, [COLLECT_PARAM]: null })

// Restores a shared or reloaded drawer link on mount, once, from the first render's values.
// `loadIssue` resolves the issue, or null on a bad id or a load error, which wipes the params instead.
const useIssueDrawerUrl = ({ isOrganization, loadIssue, onRestore }) => {
  const restoreAttempted = useRef(false)

  useEffect(() => {
    if (restoreAttempted.current)
      return
    restoreAttempted.current = true

    const rawIssueParam = readParam(ISSUE_PARAM)
    const rawCollectParam = isOrganization ? readParam(COLLECT_PARAM) : null
    const issueId = parseNumericParam(rawIssueParam)
    const collectId = parseNumericParam(rawCollectParam)
    if (!issueId || isOrganization && !collectId) {
      if (rawIssueParam || rawCollectParam)
        clearIssueParams()
      return
    }

    loadIssue(issueId, collectId).then(issue => {
      // Bail if the URL moved on while loading; restoring now would fight the user's action.
      if (Number(readParam(ISSUE_PARAM)) !== issueId)
        return
      if (issue)
        onRestore(issueId, collectId, issue)
      else
        clearIssueParams()
    })
  }, [])

  const openIssue = useCallback((issueId, collectId) => {
    updateUrlFilters({
      [ISSUE_PARAM]: issueId,
      [COLLECT_PARAM]: isOrganization ? collectId : null
    })
  }, [isOrganization])

  return { openIssue, closeIssue: clearIssueParams }
}

export default useIssueDrawerUrl
